/**
 * ActionApprovalCard Component
 *
 * Displays a pending write action that requires user approval before execution.
 * Part of the bounded autonomy flow - read tools auto-execute, write tools need approval.
 */

import Button from '@pray/shared/components/ui/Button';

import type { SolomonAction } from '../../../../types/solomon';

import styles from './ActionApprovalCard.module.scss';

export interface ActionApprovalCardProps {
  /** The pending action awaiting approval */
  action: SolomonAction;
  /** Handler for approving the action */
  onApprove: (approvalToken: string) => void;
  /** Handler for rejecting the action */
  onReject: (approvalToken: string) => void;
  /** Whether approval is being processed */
  isApproving?: boolean;
  /** Whether rejection is being processed */
  isRejecting?: boolean;
  /** Additional class name */
  className?: string;
}

export function ActionApprovalCard({
  action,
  onApprove,
  onReject,
  isApproving = false,
  isRejecting = false,
  className,
}: ActionApprovalCardProps) {
  const isPending = isApproving || isRejecting;

  // Format expiration time if present
  const formatExpiration = () => {
    if (!action.expiresAt) return null;
    const expiresAt = new Date(action.expiresAt);
    const now = new Date();
    const diffMs = expiresAt.getTime() - now.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins <= 0) return 'Expired';
    if (diffMins === 1) return '1 minute';
    return `${diffMins} minutes`;
  };

  const expirationText = formatExpiration();

  const handleApprove = () => {
    if (action.approvalToken && !isPending) {
      onApprove(action.approvalToken);
    }
  };

  const handleReject = () => {
    if (action.approvalToken && !isPending) {
      onReject(action.approvalToken);
    }
  };

  return (
    <div className={`${styles.card} ${className || ''}`}>
      <div className={styles.header}>
        <span className={styles.icon}>&#x26A0;&#xFE0F;</span>
        <span className={styles.title}>Approval Required</span>
      </div>

      <div className={styles.content}>
        <div className={styles.actionName}>
          <span className={styles.label}>Action:</span>
          <span className={styles.value}>{formatToolName(action.toolName)}</span>
        </div>

        {action.description && <p className={styles.description}>{action.description}</p>}

        {action.params && Object.keys(action.params).length > 0 && (
          <div className={styles.params}>
            <span className={styles.paramsLabel}>Parameters:</span>
            <ul className={styles.paramsList}>
              {Object.entries(action.params).map(([key, value]) => (
                <li key={key} className={styles.param}>
                  <span className={styles.paramKey}>{formatParamKey(key)}:</span>
                  <span className={styles.paramValue}>{formatParamValue(value)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {expirationText && (
          <div className={styles.expiration}>
            <span className={styles.expirationLabel}>Expires in:</span>
            <span className={styles.expirationValue}>{expirationText}</span>
          </div>
        )}
      </div>

      <div className={styles.actions}>
        <Button variant="secondary" onClick={handleReject} disabled={isPending} className={styles.rejectButton}>
          {isRejecting ? 'Rejecting...' : 'Cancel'}
        </Button>
        <Button variant="primary" onClick={handleApprove} disabled={isPending} className={styles.approveButton}>
          {isApproving ? 'Approving...' : 'Approve & Execute'}
        </Button>
      </div>
    </div>
  );
}

// Helper function to format tool names
function formatToolName(name: string): string {
  return name
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

// Helper function to format parameter keys
function formatParamKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

// Helper function to format parameter values
function formatParamValue(value: unknown): string {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export default ActionApprovalCard;
